import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import React from "react";

const FAQ: React.FC = () => {
  const faqs = [
    {
      question: "What is free in Tylee Smart Markets?",
      answer: "The news terminal, market brief, live monitor, public system status, and curated Best Of research are free to access without registration. Your own archive is tied to your browser identity or signed-in profile. New AI-generated research requires external LLM and market-data calls, so creation uses limited Guest compact capacity or your own BYOK provider keys.",
    },
    {
      question: "What does BYOK mean in Tylee Smart Markets?",
      answer: "BYOK stands for Bring Your Own Key. In Tylee Smart Markets, BYOK means you supply your own API credentials from preferred providers (LLM providers, market data APIs, chart providers, etc.). Your credentials are stored in your browser's local vault, and you can opt in to encrypted profile sync after signing in. When you request TTMA analysis or other premium features requiring external API calls, your credentials are forwarded only for the specific feature you authorize. This architecture means you control your credentials and pay providers directly for API usage.",
    },
    {
      question: "What is guest mode in Tylee Smart Markets?",
      answer: "Guest compact mode lets you preview research without providing your own LLM API credentials. It uses smaller prompts and limited server-side capacity when available. Rate limits are tighter, analysis depth is lower than BYOK, and no saved browser LLM key is sent with the request.",
    },
    {
      question: "What is the difference between Guest compact, BYOK Short Form, and BYOK Long Form?",
      answer: "Guest compact is the no-key preview path with smaller prompts and shared limits. BYOK Short Form is the default for saved provider keys; it uses your selected Gemini, Groq, or OpenRouter key with a smaller prompt to reduce time and quota use. BYOK Long Form uses your selected key with the full prompt, so it is the deepest path but can take longer and consume more provider quota.",
    },
    {
      question: "Do I need to sign in to use BYOK research?",
      answer: "No. You can keep keys in the browser vault and run BYOK research from that browser without signing in. Signing in is for encrypted profile sync and cross-device vault/archive recovery. Provider usage still depends on the keys, quotas, and limits attached to your own provider accounts.",
    },
    {
      question: "Why does Tylee Smart Markets split free and paid features?",
      answer: "TSM separates features based on infrastructure cost and sustainability. News aggregation, briefs, monitoring, status, and curated Best Of reports are inexpensive enough to keep open. Private user archives and AI research are tied to a browser identity or signed-in profile. Research calls external LLM APIs such as Gemini, Groq, or OpenRouter and may also call market-data providers such as Finnhub, Alpha Vantage, Polygon, CoinMarketCap, and FRED.",
    },
    {
      question: "What are the three research lanes?",
      answer: "Digital Asset Trader is the short-term crypto workflow using the TTMA eight-domain engine for intraday and swing-trading context. TradFi Research creates deep institutional reports for equities, commodities, indexes, rates, and macro proxies. Digital Assets Research creates long-form institutional crypto and token reports focused on network fundamentals, tokenomics, adoption, liquidity, regulation, and longer-term outlook.",
    },
    {
      question: "Why can one symbol work in one Analytics lane but not another?",
      answer: "Each lane has different guardrails. Digital Asset Trader is limited to Bitget-supported tradable crypto and digital-asset instruments. TradFi Research and Digital Assets Research can also use Yahoo Finance lookup for mainstream tickers and aliases such as NVDA, F, SPY, GOLD, XAU, XAUT, PAXG, SILVER, SPX, BTC, and ETH, while still supporting Bitget-listed tokenized or RWA-style instruments when they fit the lane. Optional Finnhub, Alpha Vantage, Polygon, CoinMarketCap, and FRED keys deepen the final research snapshot with fundamentals, earnings timing, crypto market data, official ticker references, and macro rates.",
    },
    {
      question: "Which LLM providers are supported for research?",
      answer: "Digital Asset Trader, TradFi Research, and Digital Assets Research support Groq, Gemini, and OpenRouter through the Analytics page. Gemini is used when you want Google Search grounding and source citations. Groq is the low-latency LLM path using the supplied market snapshot. OpenRouter defaults to openai/gpt-oss-120b:free first, then the openrouter/free router, so free-mode calls stay on OpenRouter's free model pool; explicit :free model slugs can be added later.",
    },
    {
      question: "What happens to reports after analysis?",
      answer: "Completed reports are saved into your Research Archive. From Analytics or the archive you can download Markdown, save a polished PDF, print, email, or reopen the report. Only reports promoted into the Featured / Best Of showcase are public for everyone.",
    },
    {
      question: "Which data sources does TSM aggregate?",
      answer: "Tylee Smart Markets aggregates headlines from over 20 premium financial publishers including the Wall Street Journal, Financial Times, Bloomberg, Reuters, CNBC, MarketWatch, Seeking Alpha, Politico, The Guardian, BBC, Al Jazeera, CoinDesk, and other regional publishers. The selection of publishers varies by market category (equities, rates, energy, crypto, macro, geopolitics, etc.) and geographic region. TSM also integrates live signals from Bloomberg terminals, CNBC, Sky News, NOAA weather data, USGS earthquake alerts, GDELT geopolitical data, WHO health alerts, and Polymarket prediction odds. All source attribution is preserved - every headline shows its original source, timestamp, and publication link.",
    },
    {
      question: "Can I export data from TSM?",
      answer: "Yes. TSM provides machine-readable data through its public API at https://tyl.ee/api. The API returns JSON responses for news items, market briefs, monitor signals, report archive metadata, and analytics results. Research output can be exported as Markdown or PDF from the web UI, emailed through the configured email workflow, or reopened later from the archive. The /llms.txt file and companion documentation are also machine-readable and designed for AI agent discovery.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | Tylee Smart Markets</title>
        <meta name="description" content="Frequently asked questions about TSM, BYOK, credentials, pricing, and how to get started with the platform." />
        <link rel="canonical" href="https://tyl.ee/faq" />
      </Helmet>

      <div className="faq-wrapper">
        <AppHeader />

        <main className="faq-main">
          <section className="faq-header">
            <h1>Frequently Asked Questions</h1>
            <p>Common questions about Tylee Smart Markets, pricing, features, and how to get started.</p>
          </section>

          <section className="faq-content">
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <details key={index} className="faq-item">
                  <summary className="faq-question">{faq.question}</summary>
                  <p className="faq-answer">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <style>{`
        .faq-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: var(--bg-primary, #0a0e27);
          color: var(--text-primary, #e0e0e0);
        }

        .faq-main {
          flex: 1;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          padding: 40px 20px;
        }

        .faq-header {
          margin-bottom: 50px;
          text-align: center;
        }

        .faq-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: var(--text-primary, #fff);
        }

        .faq-header p {
          font-size: 1.1rem;
          color: var(--text-secondary, #999);
        }

        .faq-content {
          width: 100%;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .faq-item {
          border: 1px solid var(--border-color, #333);
          border-radius: 8px;
          background: var(--bg-secondary, #111729);
          overflow: hidden;
        }

        .faq-question {
          cursor: pointer;
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 600;
          font-size: 1.1rem;
          transition: background-color 0.2s, box-shadow 0.2s;
          user-select: none;
          outline: none;
        }

        .faq-question:hover {
          background-color: var(--bg-hover, #1a2038);
        }

        .faq-question:focus-visible {
          background-color: var(--bg-hover, #1a2038);
          box-shadow: inset 0 0 0 2px #4a9eff;
        }

        .faq-question::after {
          content: '▼';
          font-size: 0.8rem;
          transition: transform 0.2s;
          margin-left: 15px;
          flex-shrink: 0;
        }

        details[open] .faq-question::after {
          transform: rotate(180deg);
        }

        .faq-answer {
          padding: 0 20px 20px 20px;
          font-size: 1rem;
          line-height: 1.6;
          color: var(--text-secondary, #bbb);
          background: var(--bg-secondary, #0f1522);
        }

        @media (max-width: 768px) {
          .faq-main {
            padding: 20px 15px;
          }

          .faq-header h1 {
            font-size: 2rem;
          }

          .faq-question {
            padding: 15px;
            font-size: 1rem;
          }

          .faq-answer {
            padding: 0 15px 15px 15px;
            font-size: 0.95rem;
          }
        }
      `}</style>
    </>
  );
};

export default FAQ;
